/**
 * The single list of route prefixes that have no per-language variant.
 *
 * Two places need this and they must never disagree: `HreflangTags` decides
 * which `<link rel="alternate">` tags to emit, and `MarketingFooter` decides
 * whether to render the visible locale switcher. If one skips a prefix and the
 * other does not, the footer links Googlebot straight at URLs that have no
 * route, and the SPA answers every one of them with HTTP 200 and the home
 * page's HTML, canonical pointing at the root.
 *
 * That is not hypothetical. Until 2026-08-02 the footer's copy of the list was
 * missing `/nvr/`, `/p/` and `/unsubscribed` while HreflangTags had them, and
 * the footer carried a comment claiming "Matches HreflangTags skipPrefixes
 * exactly". Measured consequence: each of the 693 `/nvr/` pages emitted 22
 * server-rendered anchors to `/{lang}/nvr/{slug}`, so **15,246 crawlable junk
 * URLs**, every one returning the home page under `meta robots "index, follow"`.
 * Against a legitimate sitemap of 28,347 URLs that is +54% of wasted crawl
 * surface, spent while roughly 10,900 real camera pages sat unindexed.
 *
 * A comment cannot keep two arrays in step. Importing one array can. Add a
 * prefix here and both call sites get it.
 */

import { BRAND_INDEX } from '@/data/brandIndex.generated';

/**
 * Catalog brands live at the root since the 2026-09-26 URL migration
 * (`/hikvision`, `/hikvision/page/2`, `/hikvision/ds-2cd2143g2-i`), so no
 * prefix above can match them. All of them are English-only: without this set
 * each of the 21 760 hub and camera pages emitted 32 hreflang alternates and
 * 22 footer links to `/{lang}/...` 404s (measured on the 09-26 build).
 */
const CATALOG_BRAND_SLUGS: ReadonlySet<string> = new Set(BRAND_INDEX.map(b => b.slug));

/** Route prefixes with no per-language route. Order is irrelevant. */
export const NON_TRANSLATABLE_PREFIXES: readonly string[] = [
  '/auth',
  '/dashboard',
  '/cctv-design',
  '/checkout-success',
  '/api-docs',
  '/camera/',
  // No trailing slash: `isNonTranslatablePath` then matches the hub itself
  // (`/nvr`, `/brand`) as well as every detail page under it, while still not
  // swallowing siblings like `/nvr-storage-calculator`.
  '/nvr',
  // Switches, UPS, supplies, mounts, alarm and access models: English-only
  // like /nvr, no /{lang}/equipment route.
  '/equipment',
  // Brand hubs shipped English-only (`/de/brand/hikvision` is a real 404) but
  // were never added here, so every one of the 162 hubs emitted 23 hreflang
  // alternates and 19 visible locale links straight at 404s — measured
  // 2026-08-17. hreflang pointing at missing pages is a negative signal on
  // exactly the pages that are the crawl path into the whole camera catalog.
  '/brand',
  '/compare/sku/',
  '/p/',
  '/unsubscribed',
  // English-only, found by the 2026-09-27 crawl of the 09-26 build: /de/demo
  // answers 200 with the home page (SPA fallback, canonical "/") and every
  // locale home linked it 7 times. Not /compliance or /about/facts: those take
  // their self-canonical from HreflangTags (enOnlySinglePages) and would lose it.
  '/demo',
  '/verify',
  '/404',
  // English-only SEO pages from 2026-10-09: the RTSP URL formats hub and two
  // posts written in English only. No /{lang}/ route exists for any of them,
  // so they set their own English canonical (check-page-canonical ALLOWED).
  '/rtsp-url',
  '/blog/rtsp-port',
  '/blog/cbr-vs-vbr',
  '/blog/i-frame-interval',
  '/system-surveyor-alternative',
  '/siteowl-alternative',
  '/en-50131',
  '/videocad-alternative',
  '/onvif',
  '/onvif-device-manager',
  '/rtsp',
  '/nvr-vs-dvr',
  '/poe-switch-for-ip-cameras',
  '/t568a-vs-t568b',
  '/cat6-max-length',
  '/gdpr-cctv',
];

/**
 * True when `basePath` (already stripped of any language prefix) sits under a
 * route that has no translated variant.
 *
 * Matches on the exact path or on `prefix + '/'`, never on a bare
 * `startsWith`, so `/cctv-design` does not also swallow the sibling SEO
 * landings `/cctv-design-tool` and `/cctv-design-software`.
 */
export function isNonTranslatablePath(basePath: string): boolean {
  if (CATALOG_BRAND_SLUGS.has(basePath.split('/')[1] ?? '')) return true;
  return NON_TRANSLATABLE_PREFIXES.some(
    p => basePath === p || basePath.startsWith(p.endsWith('/') ? p : `${p}/`),
  );
}
